/**
 * جدول محوري عام — الأعمدة تأتي من الخادم فتتوسّع مع أي صنف جديد
 * الأرقام لاتينية والتواريخ ميلادية (lib/date.ts)
 *
 * selectable: خانة اختيار لكل صف + صف "مجموع المحدد" — للعرض فقط، لا يُحفظ
 * ولا يؤثر على التصدير (التصدير يبقى لكل المدى)
 */
import React, { useMemo, useState } from "react";
import { formatNumber, formatDate } from "../../lib/date";
import type { PivotReport } from "../../types";

export default function PivotTable({
  report, firstColLabel = "الجهة", onRowClick, selectable = false,
}: {
  report: PivotReport;
  firstColLabel?: string;
  onRowClick?: (id: number) => void;
  selectable?: boolean;
}) {
  const [picked, setPicked] = useState<Set<number>>(new Set());

  // المحدد الفعلي = ما زال موجوداً في التقرير الحالي (بعد تغيير المدى أو المقياس)
  const selectedRows = useMemo(
    () => report.rows.filter((r) => picked.has(r.id)),
    [report.rows, picked]
  );
  const selTotals = useMemo(() => {
    const t: Record<string, number> = Object.fromEntries(report.columns.map((c) => [c.key, 0]));
    let total = 0, days = 0;
    for (const r of selectedRows) {
      for (const c of report.columns) t[c.key] = (t[c.key] ?? 0) + (r.values[c.key] ?? 0);
      total += r.total;
      days  += r.entryCount;
    }
    return { values: t, total, days };
  }, [selectedRows, report.columns]);

  if (report.rows.length === 0) {
    return (
      <div className="card text-center text-gray-400 py-10">
        لا توجد بيانات في المدى {formatDate(report.from)} — {formatDate(report.to)}
      </div>
    );
  }

  const isDiff = report.measure === "diff";
  /// في وضع الفرق: السالب نقص (أحمر)، والموجب زيادة (كهرماني)
  const tone = (n: number) =>
    !isDiff || n === 0 ? "" : n < 0 ? "text-red-700" : "text-amber-600";

  const grandTotal = Object.values(report.totals).reduce((a, b) => a + b, 0);
  const allPicked  = selectedRows.length === report.rows.length;
  const somePicked = selectedRows.length > 0 && !allPicked;

  const toggle = (id: number) =>
    setPicked((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  const toggleAll = () =>
    setPicked(allPicked ? new Set() : new Set(report.rows.map((r) => r.id)));

  return (
    <div className="card overflow-x-auto">
      <table className="w-full text-sm whitespace-nowrap">
        <thead>
          <tr className="border-b-2 border-gray-200">
            {selectable && (
              <th className="py-2 px-2 w-8 print:hidden">
                <input type="checkbox" aria-label="تحديد الكل" title="تحديد الكل"
                  checked={allPicked}
                  ref={(el) => { if (el) el.indeterminate = somePicked; }}
                  onChange={toggleAll} />
              </th>
            )}
            <th className="text-right py-2 px-2 sticky right-0 bg-white z-10">{firstColLabel}</th>
            {report.columns.map((c) => (
              <th key={c.key} className="text-center py-2 px-2 font-medium">
                <div>{c.label}</div>
                <div className="text-[10px] text-gray-400 font-normal">{c.category}</div>
              </th>
            ))}
            <th className="text-center py-2 px-2">الإجمالي</th>
            <th className="text-center py-2 px-2 text-xs text-gray-500">أيام</th>
          </tr>
        </thead>
        <tbody>
          {report.rows.map((r) => {
            const on = picked.has(r.id);
            return (
              <tr key={r.id}
                className={`border-b hover:bg-gray-50 ${on ? "bg-primary-50" : ""} ${onRowClick ? "cursor-pointer" : ""}`}
                onClick={onRowClick ? () => onRowClick(r.id) : undefined}>
                {selectable && (
                  <td className="py-2 px-2 print:hidden" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" aria-label={`تحديد ${r.name}`}
                      checked={on} onChange={() => toggle(r.id)} />
                  </td>
                )}
                <td className={`py-2 px-2 sticky right-0 ${on ? "bg-primary-50" : "bg-white"}`}>
                  {/^\d{4}-\d{2}-\d{2}$/.test(r.name) ? <span dir="ltr">{formatDate(r.name)}</span> : r.name}
                </td>
                {report.columns.map((c) => {
                  const v = r.values[c.key] ?? 0;
                  return (
                    <td key={c.key} className={`text-center py-2 px-2 ${tone(v)}`} dir="ltr">
                      {formatNumber(v)}
                    </td>
                  );
                })}
                <td className={`text-center py-2 px-2 font-semibold ${tone(r.total)}`} dir="ltr">{formatNumber(r.total)}</td>
                <td className="text-center py-2 px-2 text-xs text-gray-500" dir="ltr">{r.entryCount}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          {selectable && selectedRows.length > 0 && (
            <tr className="border-t-2 border-primary-300 font-bold bg-primary-50 text-primary-800">
              <td className="print:hidden" />
              <td className="py-2 px-2 sticky right-0 bg-primary-50">
                مجموع المحدد ({selectedRows.length})
              </td>
              {report.columns.map((c) => (
                <td key={c.key} className={`text-center py-2 px-2 ${tone(selTotals.values[c.key] ?? 0)}`} dir="ltr">
                  {formatNumber(selTotals.values[c.key] ?? 0)}
                </td>
              ))}
              <td className={`text-center py-2 px-2 ${tone(selTotals.total)}`} dir="ltr">{formatNumber(selTotals.total)}</td>
              <td className="text-center py-2 px-2 text-xs" dir="ltr">{selTotals.days}</td>
            </tr>
          )}
          <tr className="border-t-2 border-gray-300 font-bold bg-gray-50">
            {selectable && <td className="print:hidden" />}
            <td className="py-2 px-2 sticky right-0 bg-gray-50">الإجمالي</td>
            {report.columns.map((c) => (
              <td key={c.key} className={`text-center py-2 px-2 ${tone(report.totals[c.key] ?? 0)}`} dir="ltr">
                {formatNumber(report.totals[c.key] ?? 0)}
              </td>
            ))}
            <td className={`text-center py-2 px-2 ${tone(grandTotal)}`} dir="ltr">{formatNumber(grandTotal)}</td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
